import { resolveId } from '@medplum/core'
import type { QuestionnaireResponse, Reference } from '@medplum/fhirtypes'
import { formatDate } from '@seen/core/dates'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { cn } from '@seen/ui'
import { IconClipboardCheck } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { useRead } from '#src/hooks/useRead.ts'

const chipClassName =
  'select-none rounded-sm border-[0.5px] border-gray-450 bg-gray-470 box-decoration-clone px-1 py-px group-[.ProseMirror-selectednode]:bg-[lch(53_52.26_286.91/0.4)]'

export function ValidatedToolResponseMention({
  validatedToolResponse,
  className,
  displayOverride,
}: {
  validatedToolResponse: Reference<QuestionnaireResponse>
  className?: string
  displayOverride?: string | null
}): JSX.Element {
  const { data: response } = useRead(
    ValidatedToolResponse,
    validatedToolResponse
  )
  const participantId = resolveId(response?.subjectPatient)

  const content = (
    <>
      <span>
        <IconClipboardCheck className="inline-block size-[1em] align-[-0.25ex]" />
      </span>
      {displayOverride ? (
        <span> {displayOverride}</span>
      ) : (
        <span>
          {' '}
          {response?.name}
          {response && (
            <span className="text-muted-foreground">
              {' '}
              {response.displayText && <>: {response.displayText} </>}·{' '}
              {formatDate(response.authored)}
            </span>
          )}
        </span>
      )}
    </>
  )

  // The subject read resolves after the first paint; linking before then would
  // route to /Patient/undefined/...
  if (!participantId) {
    return <span className={cn(chipClassName, className)}>{content}</span>
  }

  return (
    <Link
      to={`/Patient/${participantId}/validated-tools/responses/${resolveId(validatedToolResponse)}`}
      className={cn(
        chipClassName,
        'hover:bg-gray-460 [&&]:no-underline',
        className
      )}
      tabIndex={-1}
    >
      {content}
    </Link>
  )
}
